import React, { useState } from 'react';
import { X, Copy, Check } from 'lucide-react';

interface EmbedCodeModalProps {
  isOpen: boolean;
  onClose: () => void;
  embedCode: string;
  agentName: string;
  // Plan-ComponentEmbed (2026-05-18) — agent slug used to build the
  // chat-/voice-only component snippet URLs.
  agentId?: string;
  // Gate for the "Component Embed" section. Threaded straight from the
  // SAME `agentsAPI.getEmbedCode` response Agents.tsx already fetches to
  // open this modal (NO extra network call). Verified vs SHIPPED
  // back/api/agents.py:685 (get_embed_code return) @ e8c8d5a — read-only
  // mirror of the top-level `agents.component_embed_enabled` Mongo field.
  componentEmbedEnabled?: boolean;
}

/** One copy-paste snippet block with its own copied state. */
const SnippetBlock: React.FC<{ label: string; code: string }> = ({ label, code }) => {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
        {label}
      </label>
      <div className="relative">
        <pre className="bg-gray-900 text-gray-100 p-4 rounded-xl overflow-x-auto text-sm">
          <code>{code}</code>
        </pre>
        <button
          onClick={copy}
          className="absolute top-3 right-3 btn-secondary flex items-center space-x-2"
        >
          {copied ? (
            <>
              <Check className="w-4 h-4" />
              <span>Copied!</span>
            </>
          ) : (
            <>
              <Copy className="w-4 h-4" />
              <span>Copy</span>
            </>
          )}
        </button>
      </div>
    </div>
  );
};

const EmbedCodeModal: React.FC<EmbedCodeModalProps> = ({
  isOpen,
  onClose,
  embedCode,
  agentName,
  agentId,
  componentEmbedEnabled = false,
}) => {
  const [copied, setCopied] = useState(false);

  if (!isOpen) return null;

  const handleCopy = () => {
    navigator.clipboard.writeText(embedCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Dashboard origin serves the widget SPA at /widget/<agentId> (same
  // origin Widget.tsx builds its iframe URL from).
  const origin = window.location.origin;
  const chatSnippet = `<iframe src="${origin}/widget/${agentId}?embed=chat" style="width:100%;height:600px;border:0"></iframe>`;
  // Voice needs media perms — mirror EXACTLY the allow= attrs the full
  // widget.js iframe uses (public/widget.js:424).
  const voiceSnippet = `<iframe src="${origin}/widget/${agentId}?embed=voice" style="width:100%;height:600px;border:0" allow="microphone; camera; autoplay; clipboard-write; fullscreen"></iframe>`;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-white/10">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              Embed Code
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Add this code to your website for {agentName}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Installation Instructions
            </label>
            <div className="glass rounded-xl p-4 space-y-2 text-sm text-gray-700 dark:text-gray-300">
              <p>1. Copy the code below</p>
              <p>2. Paste it before the closing <code className="bg-white/20 px-2 py-1 rounded">&lt;/body&gt;</code> tag of your website</p>
              <p>3. The chat widget will appear automatically on your site</p>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Embed Code
            </label>
            <div className="relative">
              <pre className="bg-gray-900 text-gray-100 p-4 rounded-xl overflow-x-auto text-sm">
                <code>{embedCode}</code>
              </pre>
              <button
                onClick={handleCopy}
                className="absolute top-3 right-3 btn-secondary flex items-center space-x-2"
              >
                {copied ? (
                  <>
                    <Check className="w-4 h-4" />
                    <span>Copied!</span>
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4" />
                    <span>Copy</span>
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Plan-ComponentEmbed (2026-05-18) — chat-only / voice-only
              component embeds. Renders ONLY for agents the user flagged
              `component_embed_enabled` (gate read from the shipped widget
              config). Unflagged agents never see this section. */}
          {componentEmbedEnabled && agentId && (
            <div className="space-y-4 pt-2 border-t border-gray-200 dark:border-gray-700">
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white">
                  Component Embed (chat / voice)
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  Embed ONLY the chat or ONLY the voice surface directly into
                  your own page — no launcher, fits its container.
                </p>
              </div>

              <SnippetBlock label="Chat-only embed" code={chatSnippet} />
              <SnippetBlock label="Voice-only embed" code={voiceSnippet} />

              <div className="glass rounded-xl p-4">
                <h3 className="font-semibold text-gray-900 dark:text-white mb-2">
                  Usage notes
                </h3>
                <ul className="text-sm text-gray-700 dark:text-gray-300 space-y-1">
                  <li>• Resize via the iframe <code className="bg-white/20 px-1.5 py-0.5 rounded">style</code> (width/height) — the component fills it.</li>
                  <li>• The agent must stay <strong>published</strong> for the embed to load.</li>
                  <li>• Voice requires microphone permission — the <code className="bg-white/20 px-1.5 py-0.5 rounded">allow</code> attribute is included in the voice snippet.</li>
                </ul>
              </div>
            </div>
          )}

          <div className="glass rounded-xl p-4">
            <h3 className="font-semibold text-gray-900 dark:text-white mb-2">
              💡 Pro Tips
            </h3>
            <ul className="text-sm text-gray-700 dark:text-gray-300 space-y-1">
              <li>• The widget is fully responsive and works on all devices</li>
              <li>• Customize colors and position in the AI Agent settings</li>
              <li>• Test the widget in the Playground before deploying</li>
            </ul>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end p-6 border-t border-white/10">
          <button onClick={onClose} className="btn-primary">
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

export default EmbedCodeModal;
